/**
 * Bill Runs (PRD 2026-10-21, US-006): the site filters the fetch pushes into
 * its query, and the conditions applied to the fetched rows. Pure — the page
 * keeps both in the URL, the service and the table call these.
 *
 * Site filters reuse the shared bill filter model (lib/billFilters,
 * lib/missingBills.filterSites); only Retailer is new, as a multi-select.
 */
import type { BvBill } from '../types/billTypes';
import { NO_BILL_FILTERS, matchesBillFilters, type BillFilters } from './billFilters';
import type { BillWorkflowStatus, WorkflowStep } from './billWorkflow';
import { filterSites, type ExpectedSite } from './missingBills';
import type { ReportSendStatus } from './reportStatus';
import { siteValue } from './siteFilters';
import type { BillRunRow, BillRunCoverage } from './billRunRows';
import { billRunStatus, type BillRunDetail, type BillRunStatusValue } from './billRunStatus';

// ── Site filters (pushed into the fetch) ────────────────────────────────────

export type BillRunSiteFilters = {
  /** NMI or entity (client) name, case-insensitive. Applied after the fetch. */
  q: string;
  entity: string;
  /** Multi-select; empty means every retailer. */
  retailer: string[];
  distributor: string;
  state: string;
  tariff: string;
};

export const NO_RUN_SITE_FILTERS: BillRunSiteFilters = {
  q: '', entity: '', retailer: [], distributor: '', state: '', tariff: '',
};

/** The single-value shared filters, Retailer left open (it is multi-select here). */
function sharedFilters(f: BillRunSiteFilters): BillFilters {
  return {
    ...NO_BILL_FILTERS,
    q: f.q, entity: f.entity, distributor: f.distributor, state: f.state, tariff: f.tariff,
  };
}

const NO_CONTEXT = { verdict: null, workflow: null, sendStatus: null };

/**
 * True for a site Bill Validation knows about. The `site` table is shared with
 * tendering, and a tender site has an empty bv_extn — the rule migration 153
 * applies to the BV sites list. A bill on such a site is not a BV bill.
 */
export function isBvSite(site: { bv_extn?: Record<string, unknown> | null } | null | undefined): boolean {
  return !!site?.bv_extn && Object.keys(site.bv_extn).length > 0;
}

/** True when the bill's site passes every site filter. Archiving is the fetch's
 *  concern (its archive scope), so it is not judged here. */
export function matchesRunSiteFilters(bill: Pick<BvBill, 'site' | 'bill_from' | 'bill_to'>, f: BillRunSiteFilters): boolean {
  // archived_at: null — matchesBillFilters hides archived bills unless the
  // Verdict filter asks for them; the fetch already chose the archive scope.
  if (!matchesBillFilters({ ...bill, archived_at: null }, NO_CONTEXT, sharedFilters(f))) return false;
  return f.retailer.length === 0 || f.retailer.includes(siteValue(bill, 'retailer'));
}

/** The expected sites that pass the site filters (missingBills.filterSites + Retailer). */
export function filterRunSites(sites: ExpectedSite[], f: BillRunSiteFilters): ExpectedSite[] {
  const narrowed = filterSites(sites, sharedFilters(f));
  if (f.retailer.length === 0) return narrowed;
  return narrowed.filter((s) => f.retailer.includes((s.current_retailer ?? '').trim()));
}

// ── Conditions (applied to the fetched rows) ────────────────────────────────

export type VarianceDirection = 'over' | 'under' | 'either';

/**
 * Whether the row's site was enrolled in the row's month (expectedInMonth). A
 * site is not enrolled until there is a contract and access to its meter data,
 * so its bills can't be worked yet.
 */
export type BillRunEnrolment = 'enrolled' | 'not_enrolled';
export const ENROLMENT_ORDER: BillRunEnrolment[] = ['enrolled', 'not_enrolled'];
export const ENROLMENT_LABELS: Record<BillRunEnrolment, string> = {
  enrolled: 'Enrolled',
  not_enrolled: 'Not enrolled',
};

/** Missing rows exist only for enrolled months; a bill row carries its own flag. */
export const rowEnrolment = (row: BillRunRow): BillRunEnrolment =>
  row.kind === 'missing' || row.expected ? 'enrolled' : 'not_enrolled';

export type VarianceCondition = {
  /** Minimum |variance| in dollars; null: no $ floor. */
  minDollars: number | null;
  /** Minimum |variance| as a % of the expected total; null: no % floor. */
  minPct: number | null;
  direction: VarianceDirection;
};

export type BillRunConditions = {
  /** Multi-select; empty means every retailer. */
  retailer: string[];
  status: BillRunStatusValue[];
  details: BillRunDetail[];
  /** Person-set steps on bill rows (a missing row's tag is not an Assigned Status). */
  assigned: WorkflowStep[];
  send: ReportSendStatus[];
  /** The bill's full status (lib/billWorkflow), from the Progress cards. Bill rows only. */
  workflow: BillWorkflowStatus[];
  coverage: BillRunCoverage[];
  /** Only missing site-months whose site has never had a bill. */
  neverBilled: boolean;
  /** Site enrolled in the row's month; empty means both. */
  enrolment: BillRunEnrolment[];
  /** One bill run (`bv_bill_run.id`); the page resolves its bill ids (bulkReportService.runReportBillIds). */
  billRunId: string | null;
  /** A recurring difference (lib/mismatchDrivers driverKey); the page resolves its bill ids. */
  driver: string | null;
  /** Null: not set. Set, rows without both totals are excluded (FR-8). */
  variance: VarianceCondition | null;
  /** Off by default (FR-9). */
  includeArchived: boolean;
};

export const NO_RUN_CONDITIONS: BillRunConditions = {
  retailer: [], status: [], details: [], assigned: [], send: [], workflow: [], coverage: [], neverBilled: false, enrolment: [],
  billRunId: null, driver: null, variance: null, includeArchived: false,
};

/** The condition keys a metric group can leave out (US-009). */
export type ConditionKey = Exclude<keyof BillRunConditions, 'includeArchived'>;

export type MatchOptions = {
  /** The bill ids of `conditions.billRunId`; null while not loaded (nothing matches). */
  runBillIds?: ReadonlySet<string> | null;
  /** The bill ids of `conditions.driver`; null while not worked out (nothing matches). */
  driverBillIds?: ReadonlySet<string> | null;
  /** Conditions to ignore — a metric group leaves out its own. */
  skip?: readonly ConditionKey[];
};

function rowRetailer(row: BillRunRow): string {
  return row.kind === 'bill' ? siteValue(row.board.bill, 'retailer') : (row.site.current_retailer ?? '').trim();
}

/** A missing site-month on a site that has never had a bill. */
export function isNeverBilled(row: BillRunRow): boolean {
  return row.kind === 'missing' && row.coverage === 'missing' && !row.lastBill;
}

/** True when the row passes the variance condition; rows without both totals never do. */
export function matchesVariance(row: BillRunRow, v: VarianceCondition): boolean {
  if (row.kind !== 'bill' || !row.figures) return false;
  const { variance, expected } = row.figures;
  if (v.direction === 'over' && !(variance > 0)) return false;
  if (v.direction === 'under' && !(variance < 0)) return false;
  if (v.minDollars != null && Math.abs(variance) < v.minDollars) return false;
  if (v.minPct != null) {
    if (!expected) return false;
    if ((Math.abs(variance) / Math.abs(expected)) * 100 < v.minPct) return false;
  }
  return true;
}

/** True when the row passes every condition that is set and not skipped. */
export function matchesBillRunConditions(
  row: BillRunRow,
  c: BillRunConditions,
  opts: MatchOptions = {},
): boolean {
  const skip = new Set<ConditionKey>(opts.skip ?? []);
  const on = (key: ConditionKey) => !skip.has(key);

  if (!c.includeArchived && row.kind === 'bill' && row.archived) return false;
  if (on('retailer') && c.retailer.length > 0 && !c.retailer.includes(rowRetailer(row))) return false;
  if (on('coverage') && c.coverage.length > 0 && !c.coverage.includes(row.coverage)) return false;
  if (on('neverBilled') && c.neverBilled && !isNeverBilled(row)) return false;
  if (on('enrolment') && c.enrolment.length > 0 && !c.enrolment.includes(rowEnrolment(row))) return false;

  const s = billRunStatus(row);
  if (on('status') && c.status.length > 0 && !c.status.includes(s.status)) return false;
  if (on('details') && c.details.length > 0 && !(s.details && c.details.includes(s.details))) return false;
  if (on('assigned') && c.assigned.length > 0 && !(s.assigned && c.assigned.includes(s.assigned))) return false;
  if (on('send') && c.send.length > 0 && !(row.kind === 'bill' && c.send.includes(s.send))) return false;
  if (on('workflow') && c.workflow.length > 0 && !(row.kind === 'bill' && c.workflow.includes(row.board.status))) return false;

  if (on('billRunId') && c.billRunId) {
    if (row.kind !== 'bill' || !opts.runBillIds?.has(row.board.bill.id)) return false;
  }
  if (on('driver') && c.driver) {
    if (row.kind !== 'bill' || !opts.driverBillIds?.has(row.board.bill.id)) return false;
  }
  if (on('variance') && c.variance && !matchesVariance(row, c.variance)) return false;
  return true;
}

export function hasActiveConditions(c: BillRunConditions): boolean {
  return c.retailer.length > 0 || c.status.length > 0 || c.details.length > 0 || c.assigned.length > 0
    || c.send.length > 0 || c.workflow.length > 0 || c.coverage.length > 0 || c.neverBilled || c.enrolment.length > 0
    || !!c.billRunId || !!c.driver || !!c.variance || c.includeArchived;
}
